import { createContext, useContext, ReactNode, useState, useMemo, useRef, useCallback } from "react";
import { IApiService } from "../services/ApiService";
import { Logger } from '../utils/Logger';
import { createHttpClient, createApiHelpers } from "../services/httpClient";
import { createApiService } from "../services/apiMethods";

const ApiServiceContext = createContext<IApiService | undefined>(undefined);

interface ApiServiceProviderProps {
    children: ReactNode;
}

/**
 * Provider that creates and exposes the API service to child components.
 *
 * Responsibilities:
 * - Manage authentication state (bailleurId, isProfileComplete)
 * - Create HTTP client with interceptors (via httpClient module)
 * - Create API methods (via apiMethods module)
 * - Compose and provide IApiService to the app
 *
 * Architecture:
 * - httpClient.ts: Axios instance + interceptors + HTTP helpers
 * - apiMethods.ts: All API call methods
 * - This file: Auth state management + composition
 */
export const ApiServiceProvider = ({ children }: ApiServiceProviderProps) => {
  // Auth state (useState for UI re-renders + useRef for sync access by loaders)
  // Note: bailleurId and isProfileComplete are not directly read (refs are used instead)
  // but we keep the state to trigger re-renders when auth changes
  const [_bailleurId, setBailleurId] = useState<number | undefined>(undefined);
  const [_isProfileComplete, setIsProfileComplete] = useState(false);

  // Refs for synchronous access by loaders and apiService methods
  const bailleurIdRef = useRef<number | undefined>(undefined);
  const isProfileCompleteRef = useRef(false);

  // Helper functions to update auth state with logging
  // These update refs IMMEDIATELY (synchronous) before setState
  const clearAuthState = useCallback((reason: string) => {
    Logger.debug('ApiService', `🔐 Auth state cleared - reason: ${reason}, was: bailleurId=${bailleurIdRef.current}`);
    // Update refs IMMEDIATELY (synchronous) so isAuthenticated() returns false immediately
    bailleurIdRef.current = undefined;
    isProfileCompleteRef.current = false;
    setBailleurId(undefined);
    setIsProfileComplete(false);
  }, []);

  const setAuthBailleurId = useCallback((newBailleurId: number | undefined, reason: string) => {
    Logger.debug('ApiService', `🔐 Auth state update - bailleurId: ${bailleurIdRef.current} → ${newBailleurId}, reason: ${reason}`);
    // Update ref IMMEDIATELY (synchronous) for instant effect
    bailleurIdRef.current = newBailleurId;
    setBailleurId(newBailleurId);
  }, []);

  const updateProfileCompletion = useCallback((isComplete: boolean, reason: string) => {
    Logger.debug('ApiService', `📋 Profile completion - ${isComplete ? 'COMPLETE' : 'INCOMPLETE'}, reason: ${reason}`);
    // Update ref IMMEDIATELY (synchronous) for instant effect
    isProfileCompleteRef.current = isComplete;
    setIsProfileComplete(isComplete);
  }, []);

  // Create the API service by composing modules
  const apiService: IApiService = useMemo(() => {
    // Create HTTP client with interceptors
    const httpClient = createHttpClient({
      setAuthBailleurId,
      clearAuthState
    });

    // Create HTTP helpers (apiGet, apiPost, apiDelete)
    const helpers = createApiHelpers(httpClient);

    // Create and return the complete API service
    return createApiService(
      httpClient,
      helpers,
      { bailleurIdRef, isProfileCompleteRef },
      { clearAuthState, setAuthBailleurId, updateProfileCompletion }
    );
  }, [clearAuthState, setAuthBailleurId, updateProfileCompletion]);

  return <ApiServiceContext.Provider value={apiService}>{children}</ApiServiceContext.Provider>;
};

/**
 * Hook to access the API service from context.
 * Must be used within ApiServiceProvider.
 */
export const useApiServiceContext = (): IApiService => {
    const context = useContext(ApiServiceContext);
    if (!context) {
        throw new Error("useApiServiceContext must be used within ApiServiceProvider");
    }
    return context;
};

/**
 * Alias for backward compatibility.
 * New code should use useApiServiceContext.
 */
export { useApiServiceContext as useApiService };
